import React, { useEffect, useRef } from "react";
import { pickColor, rgba, hexToRgb } from "./internal/colorUtils";

export const key = "game-of-life";

const DEFAULT_BG = "#08101e";
const DEFAULT_YOUNG = "#22d3ee";   // cells that just became alive
const DEFAULT_OLD = "#a78bfa";     // cells that have lived for a while
const DEFAULT_FADE = "#fde68a";    // ghost color of recently-dead cells

// Conway's Game of Life on a toroidal grid. Each tick:
//   • a live cell with 2 or 3 neighbors survives, otherwise it dies;
//   • a dead cell with exactly 3 neighbors is born.
// We render alive cells as filled rounded squares and recently-dead
// cells as fading ghosts so individual generations read clearly. The
// world reseeds itself every RESET_AFTER_MS — long enough to enjoy
// emergent gliders, oscillators and ash, short enough that you don't
// stare at a static still-life.
const CELL_PX = 9;                 // target cell side length in CSS px
const STEP_INTERVAL_MS = 120;      // ~8 generations / second
const FADE_GENERATIONS = 5;        // how long ghosts linger after death
const SEED_DENSITY = 0.30;         // initial fill probability
const RESET_AFTER_MS = 90_000;
const STALL_THRESHOLD = 60;        // generations without population change

function blendRgba(hexA, hexB, t, alpha) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return rgba(hexA, alpha);
  const clamp = Math.max(0, Math.min(1, t));
  const r = Math.round(a.r + (b.r - a.r) * clamp);
  const g = Math.round(a.g + (b.g - a.g) * clamp);
  const bl = Math.round(a.b + (b.b - a.b) * clamp);
  return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
}

// Sprinkle a few classic patterns on top of the random soup so there's
// guaranteed motion (gliders) and structure (block, blinker) for the
// first few seconds even on unlucky seeds.
function paintGlider(cells, cols, rows, x, y) {
  const set = (dx, dy) => {
    const cx = ((x + dx) % cols + cols) % cols;
    const cy = ((y + dy) % rows + rows) % rows;
    cells[cy * cols + cx] = 1;
  };
  // Standard SE-bound glider.
  set(1, 0); set(2, 1); set(0, 2); set(1, 2); set(2, 2);
}

function paintBlinker(cells, cols, rows, x, y) {
  cells[y * cols + ((x + cols) % cols)] = 1;
  cells[y * cols + ((x + 1 + cols) % cols)] = 1;
  cells[y * cols + ((x + 2 + cols) % cols)] = 1;
}

export default function GameOfLife({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_YOUNG,
    DEFAULT_OLD,
    DEFAULT_FADE,
  ]);
  // Mutable simulation state held on a ref so React rerenders never
  // trash the grid mid-simulation.
  const stateRef = useRef({
    cols: 0,
    rows: 0,
    cells: null,    // Uint8Array — 1 if alive
    age: null,      // Uint16Array — generations alive (0 if dead)
    fade: null,     // Uint8Array — generations since death (0 if alive or fully faded)
    next: null,     // Uint8Array — scratch buffer for next generation
    lastStep: 0,
    cycleStart: 0,
    lastPopulation: -1,
    stallCount: 0,
    cellSize: CELL_PX,
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_YOUNG),
      pickColor(colors, 2, DEFAULT_OLD),
      pickColor(colors, 3, DEFAULT_FADE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seed = (ts) => {
      const state = stateRef.current;
      const { cols, rows, cells, age, fade } = state;
      cells.fill(0);
      age.fill(0);
      fade.fill(0);
      // Random soup at SEED_DENSITY.
      for (let i = 0; i < cols * rows; i += 1) {
        if (Math.random() < SEED_DENSITY) cells[i] = 1;
      }
      // Drop a handful of guaranteed-interesting starters across the
      // grid so the first few generations are visually rich.
      const starters = Math.max(2, Math.floor((cols * rows) / 600));
      for (let s = 0; s < starters; s += 1) {
        const x = Math.floor(Math.random() * cols);
        const y = Math.floor(Math.random() * rows);
        if (Math.random() < 0.6) paintGlider(cells, cols, rows, x, y);
        else paintBlinker(cells, cols, rows, x, y);
      }
      state.cycleStart = ts;
      state.lastPopulation = -1;
      state.stallCount = 0;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const state = stateRef.current;
      const cols = Math.max(8, Math.floor(width / CELL_PX));
      const rows = Math.max(6, Math.floor(height / CELL_PX));
      state.cols = cols;
      state.rows = rows;
      state.cellSize = Math.min(width / cols, height / rows);
      state.cells = new Uint8Array(cols * rows);
      state.age = new Uint16Array(cols * rows);
      state.fade = new Uint8Array(cols * rows);
      state.next = new Uint8Array(cols * rows);
      seed(performance.now());
    };

    const step = () => {
      const state = stateRef.current;
      const { cols, rows, cells, next, age, fade } = state;
      let pop = 0;
      // Toroidal neighborhood — wrapping the indices keeps gliders
      // from disappearing at the edge and gives the world a
      // "continuous" feel.
      for (let y = 0; y < rows; y += 1) {
        const yUp = y === 0 ? rows - 1 : y - 1;
        const yDn = y === rows - 1 ? 0 : y + 1;
        const rowOff = y * cols;
        const upOff = yUp * cols;
        const dnOff = yDn * cols;
        for (let x = 0; x < cols; x += 1) {
          const xL = x === 0 ? cols - 1 : x - 1;
          const xR = x === cols - 1 ? 0 : x + 1;
          const n =
            cells[upOff + xL] + cells[upOff + x] + cells[upOff + xR] +
            cells[rowOff + xL]                     + cells[rowOff + xR] +
            cells[dnOff + xL] + cells[dnOff + x] + cells[dnOff + xR];
          const alive = cells[rowOff + x];
          const willLive = alive ? (n === 2 || n === 3) : (n === 3);
          next[rowOff + x] = willLive ? 1 : 0;
          if (willLive) {
            pop += 1;
            age[rowOff + x] = alive ? Math.min(255, age[rowOff + x] + 1) : 1;
            fade[rowOff + x] = 0;
          } else if (alive) {
            // Cell just died — start its fade timer.
            age[rowOff + x] = 0;
            fade[rowOff + x] = FADE_GENERATIONS;
          } else if (fade[rowOff + x] > 0) {
            // Already-dead cell — tick down the fade timer.
            fade[rowOff + x] -= 1;
          }
        }
      }
      // Swap cells <-> next without allocating.
      state.cells = next;
      state.next = cells;

      // Detect stalls (constant population for many generations) so we
      // can trigger an early reseed instead of staring at a still-life.
      if (pop === state.lastPopulation) state.stallCount += 1;
      else state.stallCount = 0;
      state.lastPopulation = pop;
      return pop;
    };

    const tick = (ts) => {
      const state = stateRef.current;
      const [bg, youngCol, oldCol, fadeCol] = colorsRef.current;
      if (!state.cells) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (!state.lastStep) state.lastStep = ts;
      if (!state.cycleStart) state.cycleStart = ts;

      // Advance the simulation as many generations as real time owes us
      // (mostly one per frame, more if the tab was suspended).
      while (ts - state.lastStep >= STEP_INTERVAL_MS) {
        const pop = step();
        state.lastStep += STEP_INTERVAL_MS;
        if (pop === 0 || state.stallCount > STALL_THRESHOLD || ts - state.cycleStart > RESET_AFTER_MS) {
          seed(ts);
          break;
        }
      }

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const { cols, rows, cells, age, fade, cellSize } = state;
      // Center the grid on the canvas if cells don't tile exactly.
      const offsetX = (width - cols * cellSize) / 2;
      const offsetY = (height - rows * cellSize) / 2;
      const inset = Math.max(0.5, cellSize * 0.10); // breathing room between cells

      for (let y = 0; y < rows; y += 1) {
        const py = offsetY + y * cellSize;
        for (let x = 0; x < cols; x += 1) {
          const idx = y * cols + x;
          const alive = cells[idx];
          if (alive) {
            const ageT = Math.min(1, age[idx] / 25); // normalize aging color over 25 gens
            ctx.fillStyle = blendRgba(youngCol, oldCol, ageT, 0.92);
            const px = offsetX + x * cellSize;
            ctx.fillRect(px + inset, py + inset, cellSize - 2 * inset, cellSize - 2 * inset);
          } else if (fade[idx] > 0) {
            const fadeT = fade[idx] / FADE_GENERATIONS;
            ctx.fillStyle = rgba(fadeCol, 0.32 * fadeT);
            const px = offsetX + x * cellSize;
            ctx.fillRect(px + inset, py + inset, cellSize - 2 * inset, cellSize - 2 * inset);
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
